// src/components/Cart/OrderTypeManager.jsx
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import OrderType from './OrderType';
import AddressInput from './AddressInput';
import { useConfig } from '../../contexts/ConfigContext';
import { useAppState } from '../../contexts/AppStateContext';
import { useCart } from "../../contexts/CartContext";
import { useRetailerData } from '../../hooks/dutchie-queries';
import { useUpdateCheckout } from '../../hooks/dutchie-mutations';
import { useSharedOrderType } from '../../hooks/useSharedOrderType';
import { getExistingCheckout } from '../../util';

const OrderTypeManager = ({ forcedOrderType, onValidAddressChange, className = '' }) => {
    const { retailerId } = useAppState();
    const { cartData, isFetched: isFetchedCartData, error: cartError } = useCart();
    const { data: retailerApiData, isFetched: isFetchedRetailerData, error: retailerError } = useRetailerData(retailerId);
    const [isValidAddress, setIsValidAddress] = useState(false);
    const forcedType = forcedOrderType?.orderType?.toUpperCase();
    const [orderType, setOrderType] = useSharedOrderType(forcedType);
    const initialOrderTypeSet = useRef(false);
    const checkout = getExistingCheckout();
    
    // FIXED: Calculate initialization state directly instead of using useState
    const isInitialized = useMemo(() => {
        return isFetchedRetailerData && (isFetchedCartData || cartError);
    }, [isFetchedRetailerData, isFetchedCartData, cartError]);
    
    // Determine fulfillment options from API data
    const fulfillmentOptions = useMemo(() => {
        if (!retailerApiData?.fulfillmentOptions) {
            return { showPickup: false, showDelivery: false };
        }

        const { fulfillmentOptions: opts } = retailerApiData;
        return {
            showPickup: opts.pickup || opts.curbsidePickup || opts.driveThruPickup,
            showDelivery: opts.delivery
        };
    }, [retailerApiData]);

    // CHANGED: Always show the component if at least one option is available
    const shouldShowOrderType = useMemo(() => {
        return fulfillmentOptions.showPickup || fulfillmentOptions.showDelivery;
    }, [fulfillmentOptions]);

    // Validate checkout
    const isValidCheckout = useMemo(() => {
        return Boolean(
            retailerId && 
            checkout?.retailerId && 
            checkout?.checkoutId && 
            checkout.retailerId === retailerId
        );
    }, [retailerId, checkout]);

    const handleUpdateCheckout = useUpdateCheckout(retailerId);

    // Update address
    const handleAddressUpdate = useCallback((address) => {
        if (isValidCheckout && handleUpdateCheckout && checkout) {
            handleUpdateCheckout.mutate({
                retailerId: checkout.retailerId,
                checkoutId: checkout.checkoutId,
                orderType: orderType || 'DELIVERY',
                pricingType: cartData?.pricingType || 'RECREATIONAL',
                address: {
                    street1: address.street1 || '',
                    street2: address.street2 || '',
                    city: address.city || '',
                    state: address.state || '',
                    zip: address.zip || ''
                },
            });
        }
    }, [isValidCheckout, checkout, cartData, handleUpdateCheckout, orderType]);

    // Initialize order type with priority to local storage
    useEffect(() => {
        if (!isInitialized || !fulfillmentOptions || initialOrderTypeSet.current) {
            return;
        }
    
        const { showPickup, showDelivery } = fulfillmentOptions;
        
        // Determine initial order type
        let initialType = null;
        
        // FIRST PRIORITY: Forced order type from URL params
        if (forcedOrderType?.orderType) {
            const forcedType = forcedOrderType.orderType.toUpperCase();
            if ((forcedType === 'PICKUP' && showPickup) || (forcedType === 'DELIVERY' && showDelivery)) {
                initialType = forcedType;
                // Important: Clear the localStorage value to ensure forced type takes effect
                setOrderType(forcedType);
                initialOrderTypeSet.current = true;
                return; // Exit early to prevent other priorities from overriding
            }
        }
        
        // SECOND PRIORITY: Check if we already have a value from local storage
        if (!initialType && orderType) {
            // Validate that the stored order type is available
            if ((orderType === 'PICKUP' && showPickup) || (orderType === 'DELIVERY' && showDelivery)) {
                initialType = orderType;
            } else {
                // Stored order type not available, pick first available
                initialType = showPickup ? 'PICKUP' : 'DELIVERY';
            }
        } 
        // THIRD PRIORITY: Cart data order type
        else if (!initialType && cartData?.orderType) {
            // Validate current order type is available
            if (cartData.orderType === 'PICKUP' && showPickup) {
                initialType = 'PICKUP';
            } else if (cartData.orderType === 'DELIVERY' && showDelivery) {
                initialType = 'DELIVERY';
            }
        }
        
        // FALLBACK: Default to PICKUP if available, otherwise DELIVERY
        if (!initialType) {
            initialType = showPickup ? 'PICKUP' : 'DELIVERY';
        }
    
        // Only set if different from current value to avoid unnecessary updates
        if (initialType && initialType !== orderType) {
            setOrderType(initialType);
        }
        
        initialOrderTypeSet.current = true;
    }, [isInitialized, cartData, forcedOrderType, fulfillmentOptions, orderType, setOrderType]);

    // Update checkout when order type changes (but not on initial load)
    const updateOrderType = useCallback((newOrderType) => {
        setOrderType(newOrderType);
        
        // Always update the checkout when user explicitly changes order type
        if (isValidCheckout && handleUpdateCheckout && checkout && initialOrderTypeSet.current) {
            handleUpdateCheckout.mutate({
                retailerId: checkout.retailerId,
                checkoutId: checkout.checkoutId,
                orderType: newOrderType,
                pricingType: cartData?.pricingType || 'RECREATIONAL',
                address: cartData?.address || {
                    city: '',
                    state: '',
                    street1: '',
                    street2: '',
                    zip: '',
                },
            });
        }
    }, [isValidCheckout, checkout, cartData, handleUpdateCheckout, setOrderType]);

    useEffect(() => {
        if (onValidAddressChange) {
            onValidAddressChange(isValidAddress);
        }
    }, [isValidAddress, onValidAddressChange]);

    // Don't render until initialized
    if (!isInitialized) {
        return null;
    }

    // CHANGED: Don't hide if only one option is available
    if (!shouldShowOrderType) {
        return null;
    }

    // Ensure we have a valid order type (fallback to available option)
    const displayOrderType = orderType || (fulfillmentOptions.showPickup ? 'PICKUP' : 'DELIVERY');

    return (
        <div className={`order-type-manager ${className}`}>
            <OrderType 
                selectedOrderType={displayOrderType}
                onOrderTypeChange={updateOrderType}
                fulfillmentOptions={fulfillmentOptions}
                isValidCheckout={isValidCheckout}
            />
            {displayOrderType === 'DELIVERY' && (
                <div className='headless-cart-sidebar-address'>
                    <AddressInput 
                        setIsValidAddress={setIsValidAddress}
                        cartData={cartData}
                        onAddressUpdate={handleAddressUpdate}
                        isDisabled={!isValidCheckout}
                    />
                </div>
            )}
        </div>
    );
};

export default OrderTypeManager;
